import { appFormUtil } from 'modules/common';
import React, { useEffect } from 'react';

import {
  useSaveWeatherMutation,
  SaveWeather_Input,
  useWeatherFormQuery
} from 'generated/graphql';

export type IFormState = {
  conditionId: string;
  dailyReportId: string;
  id: string;
  maxUvIndex: number;
  precipitationAmount: number;
  precipitationChange: string;
  precipitationTypeId: string;
  snowfallAmount: string;
  solarNoon: string;
  sunrise: string;
  sunset: string;
  temperatureMax: number;
  temperatureMin: number;
};

const initialState: IFormState = {
  conditionId: '',
  dailyReportId: '',
  id: '',
  maxUvIndex: 0,
  precipitationAmount: 0,
  precipitationChange: '',
  precipitationTypeId: '',
  snowfallAmount: '',
  solarNoon: '',
  sunrise: '',
  sunset: '',
  temperatureMax: 0,
  temperatureMin: 0
};

export function useFormState(id: string, dailyReportId: string) {
  const { formState, dispatch, genFieldProps } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      conditionId: {
        label: 'Condition',
        type: 'text',
        required: true
      },
      dailyReportId: {
        label: 'Daily report id',
        type: 'text'
      },
      id: {
        label: 'Id',
        type: 'text'
      },
      maxUvIndex: {
        label: 'Max uv index',
        type: 'number'
      },
      precipitationAmount: {
        label: 'Precipitation amount',
        type: 'number'
      },
      precipitationChange: {
        label: 'Precipitation change',
        type: 'text'
      },
      precipitationTypeId: {
        label: 'Precipitation',
        type: 'text'
      },
      snowfallAmount: {
        label: 'Snowfall amount',
        type: 'text'
      },
      solarNoon: {
        label: 'Solar noon',
        type: 'text'
      },
      sunrise: {
        label: 'Sunrise',
        type: 'text'
      },
      sunset: {
        label: 'Sunset',
        type: 'text'
      },
      temperatureMax: {
        label: 'Daily High Temperature',
        type: 'number'
      },
      temperatureMin: {
        label: 'Daily Low Temperature',
        type: 'number'
      }
    }
  });
  useEffect(() => {
    if (formState.dailyReportId === '' && dailyReportId !== '') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'dailyReportId',
          value: dailyReportId
        }
      });
    }
    return () => {};
  }, [
    dailyReportId,
    dispatch,
    formState.dailyReportId
  ]);

  const { data, loading, called } = useQuery(id);
  useEffect(() => {
    if (data.id && !loading && called && formState.id === '') {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...formState,
          ...data
        }
      });
    }
    return () => {};
  }, [
    called,
    data,
    dispatch,
    formState,
    loading
  ]);

  return React.useMemo(
    () => {

      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}

export function useSaveWeather() {
  const [
    fnMutate,
    tuple
  ] = useSaveWeatherMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (input: SaveWeather_Input) => {
      await fnMutate({
        variables: {
          input
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}

export interface IData extends Omit<IFormState,
'dailyReportId' |
'precipitationChange' |
'snowfallAmount' |
'solarNoon' |
'sunrise' |
'sunset'
> {}

export function useQuery(id: string) {
  const tuple = useWeatherFormQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const data: IData = {
        id: tuple.data?.weather?.id ?? '',
        conditionId: tuple.data?.weather?.condition.id ?? '',
        maxUvIndex: 0,
        precipitationTypeId: tuple.data?.weather?.precipitationType.id ?? '',
        temperatureMax: tuple.data?.weather?.temperatureMax ?? 0,
        temperatureMin: tuple.data?.weather?.temperatureMin ?? 0,
        precipitationAmount: tuple.data?.weather?.precipitationAmount ?? 0
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}
